import {
  index,
  integer,
  pgTable,
  real,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { user } from "./auth";
import { branches } from "./branches";
import { inventoryLots, inventoryMovements } from "./inventory";

export const STOCK_TRANSFER_STATUSES = [
  "DRAFT",
  "SUBMITTED",
  "REJECTED",
  "APPROVED",
  "SHIPPED",
  "RECEIVED",
  "CLOSED",
  "CANCELLED",
] as const;

export type StockTransferStatus = (typeof STOCK_TRANSFER_STATUSES)[number];

export const stockTransferRequests = pgTable(
  "stock_transfer_requests",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    requestNo: text("request_no").notNull().unique(),
    fromBranchId: text("from_branch_id")
      .notNull()
      .references(() => branches.id, { onDelete: "restrict" }),
    toBranchId: text("to_branch_id")
      .notNull()
      .references(() => branches.id, { onDelete: "restrict" }),
    status: text("status", {
      enum: STOCK_TRANSFER_STATUSES,
    }).notNull(),
    requestedByUserId: text("requested_by_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "restrict" }),
    submittedAt: timestamp("submitted_at", {
      withTimezone: true,
      mode: "date",
    }),
    reviewedByUserId: text("reviewed_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    reviewedAt: timestamp("reviewed_at", {
      withTimezone: true,
      mode: "date",
    }),
    shippedAt: timestamp("shipped_at", {
      withTimezone: true,
      mode: "date",
    }),
    receivedAt: timestamp("received_at", {
      withTimezone: true,
      mode: "date",
    }),
    closedAt: timestamp("closed_at", {
      withTimezone: true,
      mode: "date",
    }),
    note: text("note"),
    internalNote: text("internal_note"),
    rejectReason: text("reject_reason"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("stock_transfer_requests_from_status_idx").on(
      t.fromBranchId,
      t.status,
    ),
    index("stock_transfer_requests_to_status_idx").on(t.toBranchId, t.status),
    index("stock_transfer_requests_submitted_at_idx").on(t.submittedAt),
  ],
);

export const stockTransferLines = pgTable(
  "stock_transfer_lines",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    transferId: text("transfer_id")
      .notNull()
      .references(() => stockTransferRequests.id, { onDelete: "cascade" }),
    lineNo: integer("line_no").notNull(),
    itemType: text("item_type", {
      enum: ["INGREDIENT", "RETAIL_VARIANT"],
    }).notNull(),
    itemId: text("item_id").notNull(),
    qtyRequested: real("qty_requested").notNull(),
    qtyApproved: real("qty_approved"),
    qtyShipped: real("qty_shipped"),
    qtyReceived: real("qty_received"),
    unit: text("unit").notNull(),
    toBaseFactor: real("to_base_factor").notNull().default(1),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("stock_transfer_lines_transfer_idx").on(t.transferId),
    uniqueIndex("stock_transfer_lines_transfer_line_no_uidx").on(
      t.transferId,
      t.lineNo,
    ),
  ],
);

export const stockTransferShipments = pgTable(
  "stock_transfer_shipments",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    transferId: text("transfer_id")
      .notNull()
      .references(() => stockTransferRequests.id, { onDelete: "cascade" }),
    lineId: text("line_id")
      .notNull()
      .references(() => stockTransferLines.id, { onDelete: "cascade" }),
    sourceLotId: text("source_lot_id")
      .notNull()
      .references(() => inventoryLots.id, { onDelete: "restrict" }),
    qty: real("qty").notNull(),
    movementOutId: text("movement_out_id")
      .notNull()
      .references(() => inventoryMovements.id, { onDelete: "restrict" }),
    movementInId: text("movement_in_id").references(
      () => inventoryMovements.id,
      { onDelete: "set null" },
    ),
    destLotId: text("dest_lot_id").references(() => inventoryLots.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("stock_transfer_shipments_transfer_idx").on(t.transferId),
    index("stock_transfer_shipments_line_idx").on(t.lineId),
  ],
);
